// src/pages/invoicing/view-pi.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../config";
import DataTable from "react-data-table-component";

const ViewPI = () => {
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString()
    .split("T")[0];

  const [fromDate, setFromDate] = useState(firstDay);
  const [toDate, setToDate] = useState(lastDay);
  const [fromDateType, setFromDateType] = useState("text");
  const [toDateType, setToDateType] = useState("text");
  const [brandsMap, setBrandsMap] = useState({});

  useEffect(() => {
    fetch(`${API_BASE_URL}/invoicing/get_brands`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") {
          const m = {};
          (d.brands || []).forEach((b) => {
            m[b.id] = b.brand_name;
          });
          setBrandsMap(m);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "View Proforma Invoices | Webyappy";
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(
      `${API_BASE_URL}/invoicing/get_all_proformas?start_date=${fromDate}&end_date=${toDate}`,
      { credentials: "include" },
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setFiltered(data.proformas || []);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch PIs:", err);
        setLoading(false);
      });
  }, [fromDate, toDate]);

  const columns = [
    {
      name: "PI No",
      selector: (row) => row.pi_no,
      sortable: true,
    },
    {
      name: "Company",
      selector: (row) => row.to_company || "-",
      sortable: true,
    },
    {
      name: "Brand",
      selector: (row) => brandsMap[row.brand_id] || "-",
      sortable: true,
    },

    {
      name: "Amount",
      selector: (row) =>
        `₹${Math.round(parseFloat(row.grand_total || 0)).toLocaleString("en-IN")}`,
      sortable: true,
    },
    {
      name: "Generated On",
      selector: (row) =>
        new Date(row.created_on).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
      sortable: true,
    },
    {
      name: "Generated By",
      selector: (row) => row.created_by || "-",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <a
          href={`/pages/invoicing/pi-view/${row.id}`}
          target="_blank"
          rel="noopener noreferrer"
          title="View PI"
        >
          <i className="far fa-eye text-danger icon-lg"></i>
        </a>
      ),
    },
  ];

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/invoicing/Invoicing" className="text-muted">
                  Invoicing
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">
                  View Proforma Invoices
                </span>
              </li>
            </ul>
          </div>

          <div className="card card-custom shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                <h5 className="font-weight-bold mb-0">Proforma Invoice List</h5>
                <Link to="/pages/invoicing/Invoicing" className="btn btn-light">
                  <i className="fas fa-arrow-left fa-xs"></i> Back
                </Link>
              </div>

              {/* Filters */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <label className="font-weight-bold">From Date</label>
                  <input
                    type={fromDateType}
                    className="form-control"
                    value={
                      fromDateType === "date"
                        ? fromDate
                        : new Date(fromDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                    }
                    onFocus={() => setFromDateType("date")}
                    onBlur={() => setFromDateType("text")}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <label className="font-weight-bold">To Date</label>
                  <input
                    type={toDateType}
                    className="form-control"
                    value={
                      toDateType === "date"
                        ? toDate
                        : new Date(toDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                    }
                    onFocus={() => setToDateType("date")}
                    onBlur={() => setToDateType("text")}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>

                <div className="col-md-6">
                  <label className="font-weight-bold">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by PI No or Company"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filtered.filter((r) => {
                  const piNo = (r.pi_no || "").toLowerCase();
                  const company = (r.to_company || "").toLowerCase();
                  const brand = (brandsMap[r.brand_id] || "").toLowerCase();
                  const q = searchTerm.toLowerCase();
                  return (
                    piNo.includes(q) || company.includes(q) || brand.includes(q)
                  );
                })}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                striped
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewPI;
